import { useMemo } from 'react';

import { usePageCommunicationContext } from '../../../lib/stores/communication.store';
import { useAdHocRecipients, useComposeContextOptions } from '../../../lib/services/communication.service';
import { CommunicationContextType, IComposeContextSelection } from '../../../lib/types';
import { buildSessionOptionLabel } from './context-selector.helpers';

// HOOK: resolves the page-published context refs into labeled suggestions using the
// panel's own scoped queries — a ref whose entity isn't in this assessment drops out.
export const usePageContextSuggestions = (
  assessmentId?: string,
  skip = false
): { suggestions: IComposeContextSelection[]; loading: boolean } => {
  const refs = usePageCommunicationContext();

  const playerRefIds = useMemo(
    () => refs.filter((ref) => ref.type === CommunicationContextType.PLAYER_ASSESSMENT).map((ref) => ref.id),
    [refs]
  );
  const hasSessionRef = useMemo(
    () => refs.some((ref) => ref.type === CommunicationContextType.ASSESSMENT_SESSION),
    [refs]
  );

  const { sessions, loading: sessionsLoading } = useComposeContextOptions(assessmentId, skip || !hasSessionRef);
  const { items: players, loading: playersLoading } = useAdHocRecipients({
    assessmentId,
    criteria: { playerAssessmentIds: playerRefIds },
    skip: skip || !playerRefIds.length
  });

  const suggestions = useMemo<IComposeContextSelection[]>(() => {
    const sessionById = new Map(sessions.map((session) => [session._id, session]));
    const playerById = new Map(players.map((player) => [player.playerAssessmentId, player]));
    return refs.reduce<IComposeContextSelection[]>((resolved, ref) => {
      if (ref.type === CommunicationContextType.ASSESSMENT_SESSION) {
        const session = sessionById.get(ref.id);
        if (session) resolved.push({ type: ref.type, id: ref.id, label: buildSessionOptionLabel(session) });
      } else if (ref.type === CommunicationContextType.PLAYER_ASSESSMENT) {
        const player = playerById.get(ref.id);
        if (player) resolved.push({ type: ref.type, id: ref.id, label: player.playerName });
      }
      return resolved;
    }, []);
  }, [refs, sessions, players]);

  // Loading while a query needed to resolve the current refs is still in flight.
  const loading = (hasSessionRef && sessionsLoading) || (playerRefIds.length > 0 && playersLoading);

  return { suggestions, loading };
};
